import React, { useMemo } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Progress, Note, Avatar, AwardBadge } from "./ui";
import { Icon } from "./Icons";
import { calcPorTime, statusMetaColetiva, calcPontosCorretor, calcAwardVitalicio } from "./awards";

// ═══════════════════════════════════════════════════════════════════════
// TIMES — meta semanal (Caminho A: Volume R$ + Ativações)
// ═══════════════════════════════════════════════════════════════════════

export default function Times({ corretores, metas, times: timesRuntime }) {
  const times = useMemo(
    () => calcPorTime(corretores, timesRuntime).sort((a, b) => b.valor - a.valor),
    [corretores, timesRuntime]
  );
  const m = metas.time;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Ranking de Times"
        subtitle={`Ciclo semanal (dom→sáb · zera domingo 00h) · Metas: ${fmt.brl(m.metaVolume)} em volume E ${m.metaAtivacoes} ativações · Supermeta = bate as duas`}
        icon={<Icon.team size={24} />}
      />

      <div style={{ display: "grid", gap: 10 }}>
        {m.premioVolume && (
          <Note tone="brand" icon={<Icon.gift size={14} />}>
            <b>Meta Volume:</b> {m.premioVolume}
          </Note>
        )}
        {m.premioAtivacoes && (
          <Note tone="info" icon={<Icon.users size={14} />}>
            <b>Meta Ativações:</b> {m.premioAtivacoes}
          </Note>
        )}
        {m.premioSupermeta && (
          <Note tone="ok" icon={<Icon.rocket size={14} />}>
            <b>🏆 Supermeta (bateu as 2):</b> {m.premioSupermeta}
          </Note>
        )}
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {times.map((grp, i) => (
          <TimeCard key={grp.time.id} grupo={grp} posicao={i + 1} metas={m} />
        ))}
      </div>

      {times.length === 0 && (
        <Card>
          <div style={{ textAlign: "center", padding: 40, color: T.textDim, fontSize: 13 }}>
            Nenhum time ativo no momento.
          </div>
        </Card>
      )}
    </div>
  );
}

function TimeCard({ grupo, posicao, metas }) {
  const membrosQtd = grupo.membros.length;
  const timeValido = membrosQtd >= 3;

  const st = timeValido ? statusMetaColetiva({
    atingidoVol: grupo.valor,
    atingidoAtiv: grupo.ativados,
    metaVol: metas.metaVolume,
    metaAtiv: metas.metaAtivacoes,
  }) : "incompleto";

  const statusInfo = {
    supermeta:  { label: "Supermeta 🏆",        color: T.ok,     icon: "🏆" },
    volume:     { label: "Meta volume ✓",        color: T.brand,  icon: "💰" },
    ativacoes:  { label: "Meta ativações ✓",     color: T.info,   icon: "👥" },
    parcial:    { label: "Em progresso",         color: T.textSub, icon: "○" },
    incompleto: { label: `Incompleto · falta${membrosQtd === 2 ? "" : "m"} ${3 - membrosQtd} corretor(es)`, color: T.warn, icon: "⚠" },
  }[st];

  const pctVol  = Math.min((grupo.valor    / metas.metaVolume   ) * 100, 100);
  const pctAtiv = Math.min((grupo.ativados / metas.metaAtivacoes) * 100, 100);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: T.r.md,
            background: T.cardSoft, border: `1px solid ${T.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 26, flexShrink: 0,
          }}>{grupo.time.emoji || "🏆"}</div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2, flexWrap: "wrap" }}>
              {posicao <= 3 ? (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "3px 10px", borderRadius: 100,
                  background: posicao === 1 ? T.gold + "33" : posicao === 2 ? "#c0c0c033" : "#cd7f3233",
                  border: `1px solid ${posicao === 1 ? T.gold : posicao === 2 ? "#c0c0c0" : "#cd7f32"}66`,
                  fontSize: 11, fontWeight: 700,
                  color: posicao === 1 ? T.gold : posicao === 2 ? "#c0c0c0" : "#cd7f32",
                  letterSpacing: 0.3,
                }}>
                  {posicao === 1 ? "🥇" : posicao === 2 ? "🥈" : "🥉"} #{posicao}
                </span>
              ) : (
                <span className="serif" style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 22, height: 22, borderRadius: "50%",
                  background: "transparent",
                  color: T.textSub, fontSize: 11, fontWeight: 700,
                }}>#{posicao}</span>
              )}
              <div className="serif" style={{ fontSize: 20, fontWeight: 600, color: T.brand, letterSpacing: -0.3 }}>
                {grupo.time.nome}
              </div>
            </div>
            <div style={{ fontSize: 11, color: T.textSub, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon.pin size={10} />{grupo.time.cidade}-{grupo.time.uf}
              </span>
              <span style={{ color: T.textDim }}>·</span>
              <span>{grupo.membros.length} {grupo.membros.length === 1 ? "corretor" : "corretores"}</span>
            </div>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: statusInfo.color,
            padding: "4px 12px", borderRadius: 100,
            background: statusInfo.color + "14",
            border: `1px solid ${statusInfo.color}44`,
            letterSpacing: 0.5, textTransform: "uppercase",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            {statusInfo.icon} {statusInfo.label}
          </div>
        </div>
      </div>

      {/* DUAS barras de progresso lado a lado */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Meta Volume */}
        <div style={{
          background: pctVol >= 100 ? T.brandSoft : T.input,
          border: `1px solid ${pctVol >= 100 ? T.brand + "55" : T.border}`,
          borderRadius: T.r.md, padding: 14,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>
              💰 Volume
            </div>
            <div style={{ fontSize: 11, color: pctVol >= 100 ? T.ok : T.textSub, fontWeight: 700 }}>
              {pctVol >= 100 ? "✓ bateu" : `${pctVol.toFixed(0)}%`}
            </div>
          </div>
          <div className="serif" style={{
            fontSize: 20, fontWeight: 600, color: T.text, lineHeight: 1,
            marginBottom: 6,
          }}>
            {fmt.brl(grupo.valor)}
          </div>
          <Progress value={grupo.valor} max={metas.metaVolume} color={T.brand} height={5} />
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
            Meta: {fmt.brl(metas.metaVolume)}
          </div>
        </div>

        {/* Meta Ativações */}
        <div style={{
          background: pctAtiv >= 100 ? T.infoSoft : T.input,
          border: `1px solid ${pctAtiv >= 100 ? T.info + "55" : T.border}`,
          borderRadius: T.r.md, padding: 14,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>
              👥 Ativações
            </div>
            <div style={{ fontSize: 11, color: pctAtiv >= 100 ? T.ok : T.textSub, fontWeight: 700 }}>
              {pctAtiv >= 100 ? "✓ bateu" : `${pctAtiv.toFixed(0)}%`}
            </div>
          </div>
          <div className="serif" style={{
            fontSize: 20, fontWeight: 600, color: T.text, lineHeight: 1,
            marginBottom: 6,
          }}>
            {grupo.ativados} <span style={{ fontSize: 12, color: T.textSub }}>vivas</span>
          </div>
          <Progress value={grupo.ativados} max={metas.metaAtivacoes} color={T.info} height={5} />
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
            Meta: {metas.metaAtivacoes} ativações
          </div>
        </div>
      </div>

      {/* Estatísticas extras */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 14 }}>
        <Stat label="Vendas" value={grupo.vendas} />
        <Stat label="Pontos time" value={grupo.pts} />
        <Stat label="Cadastrados" value={grupo.cadastrados} />
      </div>

      {/* Membros do time */}
      {grupo.membros.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
            Membros
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {grupo.membros
              .map(c => ({ ...c, pts: calcPontosCorretor(c).total, award: calcAwardVitalicio(c) }))
              .sort((a, b) => b.pts - a.pts)
              .map(c => {
                const initials = c.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <div key={c.id} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: T.cardSoft, borderRadius: 20, padding: "4px 10px 4px 4px",
                    border: `1px solid ${T.border}`,
                  }}>
                    <Avatar initials={initials} size={24} photo={c.foto} />
                    <span style={{ fontSize: 11, color: T.text }}>{c.nome.split(" ")[0]}</span>
                    <span style={{ fontSize: 11, color: T.brand, fontWeight: 600 }}>{c.pts}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: "8px 12px" }}>
      <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
      <div className="serif" style={{ fontSize: 16, fontWeight: 600, color: T.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}
